import { useCallback } from 'react';

import { useApi } from 'app/shared/contexts/api';

/**
 * Shape consumed by every function in `@hqo/service-booking-client`. The
 * package exports a `AuthorizationOptions` union, but importing the type here
 * just to re-export it would tie this hook to the package's internal layout —
 * we mirror only the `authToken` arm because tenant FE never needs the
 * `internalToken` (service-to-service) variant.
 */
export interface ServiceBookingAuthorizationOptions {
  authToken: string;
}

/**
 * Returns a builder that lazily produces the `AuthorizationOptions` payload
 * the client lib's `sendRequest` util consumes. The token is captured at call
 * time rather than at hook mount so a refresh during the page's lifetime
 * still ships the latest bearer.
 *
 * Caller pattern:
 * ```ts
 * const getAuthOptions = useServiceBookingAuth();
 * const result = await getEvent({ baseUrl, eventUuid }, await getAuthOptions());
 * ```
 */
export const useServiceBookingAuth = (): (() => Promise<ServiceBookingAuthorizationOptions>) => {
  const { getAuthToken } = useApi();
  return useCallback(async () => ({ authToken: await getAuthToken() }), [getAuthToken]);
};
